// The signed-in account (if any). Guests are fully supported; signing up later keeps their progress.
// When the account cannot be checked (the server or its database does not answer, or the device is offline) the
// session is "unavailable": whatever was known stays, nobody is shown as signed out or as free, and it retries.

import type { ReactNode } from 'react'
import type { Access } from '@/lib/plans'

import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState
} from 'react'
import { GUEST_ACCESS } from '@/lib/plans'
import { browserTimeZone } from '@/lib/time-zone'

export type Role = 'learner' | 'editor' | 'reviewer' | 'admin'

export interface SessionUser {
    id: string
    email: string
    displayName: string
    role: Role
    mode: 'kids' | 'adults'
    explanationLang: 'hi' | 'en' | 'both'
    showTranslit: boolean
    leaderboardEnabled: boolean
    /** The account confirmed its email (needed for a staff role; reset links always go to this address). */
    emailVerified: boolean
    /** The time zone the XP day and streak follow (sent by this browser after sign-in). */
    timeZone?: string | null
}

interface SessionContextValue {
    user: SessionUser | null
    /** What this visitor may open (plans and trial); the free part until the session has loaded. */
    access: Access
    loading: boolean
    /** The account or its Premium could not be checked just now: say so instead of offering sign-in or Premium. */
    unavailable: boolean
    refresh: () => Promise<SessionUser | null>
    signOut: () => Promise<void>
}

const SessionContext = createContext<SessionContextValue | null>(null)

/** Seconds between checks while the account cannot be reached. */
const RETRY_SECONDS = [15, 30, 60, 120, 300]

let sentTimeZone: string | null = null

/** Tells the account which time zone this device is in (once per visit), so XP days and streaks follow local midnight. */
const syncTimeZone = (user: SessionUser | null): void => {
    if (!user) return

    const zone = browserTimeZone()

    if (user.timeZone === zone || sentTimeZone === zone) return

    sentTimeZone = zone
    fetch('/api/me/prefs', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ timezone: zone })
    }).catch(() => {
        sentTimeZone = null
    })
}

export const SessionProvider = ({ children }: { children: ReactNode }): ReactNode => {
    const [user, setUser] = useState<SessionUser | null>(null)
    const [access, setAccess] = useState<Access>(GUEST_ACCESS)
    const [loading, setLoading] = useState(true)
    const [unavailable, setUnavailable] = useState(false)
    const userRef = useRef<SessionUser | null>(null)
    const retryTimer = useRef<number | null>(null)
    const retries = useRef(0)
    const refreshRef = useRef<() => Promise<SessionUser | null>>(async () => null)

    const clearRetry = () => {
        if (retryTimer.current) window.clearTimeout(retryTimer.current)

        retryTimer.current = null
    }

    const scheduleRetry = useCallback(() => {
        if (retryTimer.current) return

        const wait = RETRY_SECONDS[Math.min(retries.current, RETRY_SECONDS.length - 1)]

        retries.current += 1
        retryTimer.current = window.setTimeout(() => {
            retryTimer.current = null
            void refreshRef.current()
        }, wait * 1000)
    }, [])

    const refresh = useCallback(async () => {
        try {
            const res = await fetch('/api/auth/me', { cache: 'no-store' })

            // The server could not check the session: unknown, not signed out. Keep what we had and try again.
            if (res.status >= 500) {
                setUnavailable(true)
                scheduleRetry()

                return userRef.current
            }

            const data = res.ok ? await res.json() as { user: SessionUser | null, access?: Access } : { user: null }
            const next = data.user
            const nextAccess = data.access ?? GUEST_ACCESS

            userRef.current = next
            setUser(next)
            syncTimeZone(next)
            setAccess(nextAccess)
            setUnavailable(Boolean(nextAccess.unavailable))

            if (nextAccess.unavailable) {
                scheduleRetry()
            }
            else {
                retries.current = 0
                clearRetry()
            }

            return next
        }
        catch {
            // Offline or no answer at all: keep what we had.
            setUnavailable(true)
            scheduleRetry()

            return userRef.current
        }
        finally {
            setLoading(false)
        }
    }, [scheduleRetry])

    refreshRef.current = refresh

    useEffect(() => {
        refresh()

        const onOnline = () => {
            clearRetry()
            void refreshRef.current()
        }

        window.addEventListener('online', onOnline)

        return () => {
            window.removeEventListener('online', onOnline)
            clearRetry()
        }
    }, [refresh])

    const signOut = useCallback(async () => {
        await fetch('/api/auth/logout', { method: 'POST' }).catch(() => undefined)
        userRef.current = null
        clearRetry()
        setUser(null)
        setAccess(GUEST_ACCESS)
        setUnavailable(false)
    }, [])

    const value = useMemo(() => ({ user, access, loading, unavailable, refresh, signOut }), [user, access, loading, unavailable, refresh, signOut])

    return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>
}

export const useSession = (): SessionContextValue => {
    const ctx = useContext(SessionContext)

    if (!ctx) throw new Error('useSession must be used inside SessionProvider')

    return ctx
}

export const canReview = (user: SessionUser | null): boolean => user?.role === 'reviewer' || user?.role === 'admin'
export const canEdit = (user: SessionUser | null): boolean => canReview(user) || user?.role === 'editor'
